if (!customElements.get('pdp-quantity-upsell')) {
  customElements.define('pdp-quantity-upsell', class extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('product-form-component');
      if (this.closest('dialog, quick-add-modal, quick-add-dialog, .quick-add-modal')) {
        this.hidden = true;
        return;
      }
      this.variants = JSON.parse(this.querySelector('[data-quantity-variants]')?.textContent || '[]');
      const tiers = JSON.parse(this.querySelector('[data-quantity-discounts]')?.textContent || 'null');
      this.discountTiers = Array.isArray(tiers) ? tiers : [];
      this.formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: this.dataset.currency || 'USD',
      });
      this.onChoice = (event) => {
        this.showError('');
        if (event.target.matches('[data-quantity-choice]')) this.setQuantity();
        this.renderSelection();
        this.syncGuidedState();
        if (event.target.matches('[data-size-choice]')) this.closeSizeMenu(event.target.closest('[data-size-menu]'), true);
      };
      this.onSizesTransition = (event) => {
        if (event.target.matches('[data-sizes-reveal], [data-purchase-reveal]') && event.propertyName === 'grid-template-rows') event.target.dataset.settled = 'true';
      };
      this.onSizeMenuToggle = (event) => {
        if (!event.target.matches?.('[data-size-menu]') || !event.target.open) return;
        for (const reveal of this.querySelectorAll('[data-sizes-reveal], [data-purchase-reveal]')) {
          reveal.getAnimations().forEach((animation) => animation.finish());
          reveal.dataset.settled = 'true';
        }
        this.querySelector('[data-guidance]')?.getAnimations().forEach((animation) => animation.finish());
      };
      this.onPickerChange = (event) => {
        const picker = event.target.closest?.('variant-picker[ref="mainVariantPicker"]');
        if (picker?.dataset.productId !== this.dataset.productId) return;
        this.syncGuidedState();
      };
      this.onVariant = (event) => {
        if (String(event.detail?.data?.productId) !== this.dataset.productId) return;
        window.setTimeout(() => {
          delete this.dataset.variantPending;
          this.refresh(String(event.detail?.resource?.id || ''));
        }, 0);
      };
      this.onVariantPending = (event) => {
        if (event.target.closest?.('variant-picker')?.dataset.productId === this.dataset.productId) {
          this.dataset.variantPending = 'true';
          this.dataset.ready = 'false';
        }
      };
      this.onCart = (event) => window.setTimeout(() => {
        if (event.detail?.resource?.items) {
          for (const variant of this.variants) {
            variant.cart_quantity = event.detail.resource.items
              .filter((item) => String(item.variant_id) === String(variant.id))
              .reduce((sum, item) => sum + item.quantity, 0);
          }
        }
        this.refresh();
      }, 0);
      this.onPageShow = () => window.requestAnimationFrame(() => {
        this.setQuantity();
        this.refresh();
      });
      this.onLockedChoice = (event) => {
        if (!this.isGuidedChoice() || this.optionsReady() || !event.target.closest?.('.pdp-quantity-upsell__choice')) return;
        event.preventDefault();
        document.dispatchEvent(new CustomEvent('pusa:request-size', { detail: { productId: this.dataset.productId } }));
      };
      this.onHelpDismiss = (event) => {
        if (event.type === 'pointerdown') this.pointerModality = true;
        else if (!event.metaKey && !event.ctrlKey && !event.altKey) this.pointerModality = false;
        for (const menu of this.querySelectorAll('[data-size-menu][open]')) {
          if (event.type === 'keydown' && event.key === 'Escape') {
            this.closeSizeMenu(menu, true);
            event.preventDefault();
          } else if (event.type === 'pointerdown' && !menu.contains(event.target)) this.closeSizeMenu(menu);
        }
        for (const help of this.querySelectorAll('[data-quantity-help][open]')) {
          if (event.type === 'keydown') {
            if (event.key !== 'Escape') return;
            help.open = false;
            help.querySelector('summary')?.focus();
            event.preventDefault();
          } else if (!help.contains(event.target)) help.open = false;
        }
      };
      this.addEventListener('click', this.onLockedChoice, true);
      this.addEventListener('change', this.onChoice);
      this.addEventListener('transitionend', this.onSizesTransition);
      this.addEventListener('toggle', this.onSizeMenuToggle, true);
      document.addEventListener('change', this.onPickerChange);
      document.addEventListener('variant:update', this.onVariant);
      document.addEventListener('variant:selected', this.onVariantPending);
      document.addEventListener('cart:update', this.onCart);
      document.addEventListener('pointerdown', this.onHelpDismiss);
      document.addEventListener('keydown', this.onHelpDismiss);
      window.addEventListener('pageshow', this.onPageShow);
      this.refresh(this.dataset.variantId);
      window.setTimeout(() => this.syncGuidedState(), 0);
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.syncGuidedState(), { once: true });
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onLockedChoice, true);
      this.removeEventListener('change', this.onChoice);
      this.removeEventListener('transitionend', this.onSizesTransition);
      this.removeEventListener('toggle', this.onSizeMenuToggle, true);
      document.removeEventListener('change', this.onPickerChange);
      document.removeEventListener('variant:update', this.onVariant);
      document.removeEventListener('variant:selected', this.onVariantPending);
      document.removeEventListener('cart:update', this.onCart);
      document.removeEventListener('pointerdown', this.onHelpDismiss);
      document.removeEventListener('keydown', this.onHelpDismiss);
      window.removeEventListener('pageshow', this.onPageShow);
    }

    get input() {
      return this.form?.querySelector('quantity-selector-component input[name="quantity"]');
    }

    get selectedCount() {
      const selected = this.querySelector('[data-quantity-choice]:checked');
      return selected ? Number(selected.value) : this.dataset.guided === 'true' ? 0 : 1;
    }

    get quantityChoices() {
      return [...this.querySelectorAll('[data-quantity-choice]')];
    }

    getSizeChoice(slot) {
      return this.querySelector(`[data-size-slot="${slot}"] [data-size-choice]:checked`)?.value || '';
    }

    closeSizeMenu(menu, focus = false) {
      if (!menu) return;
      menu.open = false;
      if (focus) menu.querySelector('summary')?.focus({ preventScroll: true });
    }

    setSizesExpanded(expanded) {
      this.setPanelExpanded('[data-sizes-reveal]', expanded);
    }

    setPanelExpanded(selector, expanded) {
      const reveal = this.querySelector(selector);
      if (!reveal || this.dataset.guided !== 'true') return;
      reveal.inert = !expanded;
      reveal.setAttribute('aria-hidden', String(!expanded));
      if (reveal.dataset.expanded === String(expanded)) return;
      reveal.dataset.settled = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'true' : 'false';
      reveal.dataset.expanded = String(expanded);
    }

    syncSizeMenus() {
      for (const row of this.querySelectorAll('[data-size-row]')) {
        const menu = row.querySelector('[data-size-menu]');
        const radio = row.querySelector('[data-size-choice]:checked');
        const variant = this.variants.find((item) => String(item.id) === radio?.value);
        const label = row.querySelector('[data-size-menu-label]');
        if (label) label.textContent = variant?.title || `Choose ${this.dataset.selectionNoun}`;
        const trigger = menu?.querySelector('summary');
        if (trigger) trigger.setAttribute('aria-label', `${variant?.title ? `${this.dataset.selectionNoun} ${variant.title}` : `Choose ${this.dataset.selectionNoun}`} for ${this.dataset.itemNoun} ${row.dataset.sizeRow}`);
        if (row.hidden) this.closeSizeMenu(menu);
      }
    }

    get currentVariant() {
      const id = this.form?.querySelector('input[name="id"]')?.value;
      return this.variants.find((item) => String(item.id) === String(id));
    }

    get cartCount() {
      return this.variants.reduce((sum, variant) => sum + Number(variant.cart_quantity || 0), 0);
    }

    discountForQuantity(units) {
      const totalUnits = units;
      const cardDiscount = this.quantityChoices.reduce((best, tier) =>
        Number(tier.value) <= totalUnits ? Math.max(best, Number(tier.dataset.baseDiscountPercent || 0)) : best, 0
      );
      return this.discountTiers.reduce((best, tier) => {
        const quantity = Number(tier.quantity);
        const percent = Math.min(99, Math.max(0, Number(tier.percent) || 0));
        return quantity > 0 && quantity <= totalUnits ? Math.max(best, percent) : best;
      }, cardDiscount);
    }

    cartDiscountForQuantity(units) {
      return this.discountForQuantity(this.cartCount + units);
    }

    get remainingByVariant() {
      return this.variants.map((variant) => {
        if (!variant.available) return { id: variant.id, remaining: 0 };
        const orderLimit = variant.max == null ? Infinity : Math.max(0, Number(variant.max) - Number(variant.cart_quantity || 0));
        const stock = variant.inventory_management === 'shopify' && variant.inventory_policy === 'deny'
          ? Math.max(0, Number(variant.inventory) - Number(variant.cart_quantity || 0))
          : Infinity;
        return { id: variant.id, remaining: Math.min(orderLimit, stock) };
      });
    }

    refresh(variantId) {
      const input = this.input;
      if (!input) return;
      const variant = this.variants.find((item) => String(item.id) === String(variantId)) || this.currentVariant;
      const limits = this.remainingByVariant;
      const primaryRemaining = limits.find((item) => String(item.id) === String(variant?.id))?.remaining || 0;
      const mixed = this.dataset.allowMix === 'true' && !!this.querySelector('[data-quantity-sizes]');
      const totalRemaining = mixed
        ? limits.reduce((sum, item) => sum + item.remaining, 0)
        : primaryRemaining;
      const standardRule = Number(input.min) === 1 && Number(input.step) === 1;
      this.hidden = !variant?.available || !standardRule || totalRemaining < 1;
      if (this.hidden) {
        delete this.dataset.multi;
        if (variant) this.form.dataset.kitItemPrice = String(variant.price);
        this.syncGuidedState();
        return;
      }

      const choices = this.quantityChoices;
      const optionsReady = this.optionsReady();
      for (const choice of choices) {
        choice.disabled = Number(choice.value) > totalRemaining || (this.dataset.guided === 'true' && !optionsReady);
      }
      if (this.dataset.guided === 'true') {
        const selected = choices.find((choice) => choice.checked);
        if (selected && Number(selected.value) > totalRemaining) selected.checked = false;
      } else {
        const current = Number(input.value);
        const matching = choices.find((choice) => !choice.disabled && Number(choice.value) === current);
        const next = matching || choices.find((choice) => !choice.disabled);
        if (!next) {
          this.hidden = true;
          return;
        }
        next.checked = true;
        if (!matching) this.setQuantity();
      }
      this.renderSelection(variant);
      this.syncGuidedState();
    }

    renderSelection(variant = this.currentVariant) {
      if (!variant) return;
      const count = this.selectedCount;
      this.dataset.hasCart = String(this.cartCount > 0);
      const mixed = this.isMixedChoice();
      this.dataset.multi = mixed ? 'true' : 'false';
      const note = this.querySelector('[data-offer-note]');
      if (note) note.textContent = note.dataset.offerNote;
      const sizes = this.querySelector('[data-quantity-sizes]');
      if (sizes) {
        sizes.hidden = !mixed;
        for (const row of sizes.querySelectorAll('[data-size-row]')) {
          const active = Number(row.dataset.sizeRow) <= count;
          row.hidden = !active;
          row.querySelectorAll('[data-size-choice]').forEach((radio) => { radio.disabled = !active; });
        }
      }
      for (const choice of this.quantityChoices) {
        const units = Number(choice.value);
        const percent = this.discountForQuantity(units);
        const cartPercent = this.cartDiscountForQuantity(units);
        choice.dataset.discountPercent = String(cartPercent);
        const label = choice.closest('label');
        const name = label.querySelector('[data-quantity-name]');
        if (name) name.textContent = `${units} ${this.dataset.itemNoun}${units === 1 ? '' : 's'}`;
        const badge = label.querySelector('[data-quantity-badge]');
        if (badge) {
          const recommended = units === Number(this.dataset.recommendedQuantity ?? 2) && percent > 0;
          const otherDiscounts = this.quantityChoices.filter((tier) => Number(tier.value) !== units)
            .map((tier) => this.discountForQuantity(Number(tier.value)));
          const bestValue = !recommended && units === Number(this.dataset.bestValueQuantity ?? 3)
            && percent > Math.max(0, ...otherDiscounts);
          badge.hidden = !recommended && !bestValue;
          badge.textContent = recommended ? 'Recommended' : bestValue ? 'Best value' : '';
          badge.classList.toggle('pdp-quantity-upsell__choice-badge--recommended', recommended);
          badge.classList.toggle('pdp-quantity-upsell__choice-badge--best-value', bestValue);
          label.classList.toggle('pdp-quantity-upsell__choice--recommended', recommended);
        }
        const picked = [variant];
        if (mixed && units === count) {
          for (let slot = 2; slot <= units; slot++) {
            const id = this.getSizeChoice(slot);
            const extra = this.variants.find((item) => String(item.id) === id);
            if (extra) picked.push(extra);
          }
        }
        while (picked.length < units) picked.push(variant);
        const total = picked.reduce((sum, item) => sum + Number(item.price) - Math.floor(Number(item.price) * percent / 100), 0);
        const unit = choice.closest('label').querySelector('[data-quantity-unit]');
        if (unit) unit.textContent = this.formatter.format(Math.round(total / units) / 100);
        const perk = choice.closest('label').querySelector('[data-quantity-free-shipping]');
        const freeThreshold = Number(this.dataset.freeThresholdCents || 0);
        if (perk) perk.hidden = !(freeThreshold > 0 && total >= freeThreshold);
        if (choice.checked) {
          const cartTotal = picked.reduce((sum, item) => sum + Number(item.price) - Math.floor(Number(item.price) * cartPercent / 100), 0);
          this.form.dataset.kitItemPrice = String(Math.round(cartTotal / units));

        }
      }
    }

    get picker() {
      return this.closest('.shopify-section')?.querySelector(
        `variant-picker[ref="mainVariantPicker"][data-product-id="${this.dataset.productId}"]`
      );
    }

    optionsReady() {
      const picker = this.picker;
      if (!picker) return this.variants.length === 1;
      const fields = [...picker.querySelectorAll('fieldset[data-option-name]')];
      return fields.every((field) => {
        const select = field.querySelector('select');
        return select ? !!select.value : !!field.querySelector('input[type="radio"]:checked');
      });
    }

    isGuidedChoice() {
      return this.dataset.guided === 'true' && !this.hidden;
    }

    isReady() {
      return !this.isGuidedChoice() || this.dataset.ready === 'true';
    }

    // Guided purchase steps: number each visible picker legend and the quantity heading
    // (pending numeral → current numeral → check). Re-run after picker morphs replace legends.
    syncSteps(ready) {
      const heading = this.querySelector('.pdp-quantity-upsell__heading');
      const legends = [...(this.picker?.querySelectorAll('fieldset[data-option-name] > legend:not(.visually-hidden)') || [])];
      if (this.hidden || !heading || !legends.length) {
        for (const el of [heading, ...legends]) if (el) { delete el.dataset.step; delete el.dataset.stepState; }
        return;
      }
      let currentFound = false;
      const mark = (el, step, done) => {
        el.dataset.step = String(step);
        el.dataset.stepState = done ? 'done' : currentFound ? 'upcoming' : 'current';
        if (!done) currentFound = true;
      };
      legends.forEach((legend, index) => {
        const field = legend.parentElement;
        const select = field.querySelector('select');
        mark(legend, index + 1, select ? !!select.value : !!field.querySelector('input[type="radio"]:checked'));
      });
      mark(heading, legends.length + 1, ready);
    }

    syncGuidedState() {
      if (this.dataset.guided !== 'true' || !this.form) return;
      const active = !this.hidden;
      const optionsReady = this.optionsReady();
      const choice = this.querySelector('[data-quantity-choice]:checked:not(:disabled)');
      const count = Number(choice?.value || 0);
      const limits = this.remainingByVariant;
      this.updateExtraSizeAvailability(limits, count);
      const picked = Array(this.isMixedChoice() ? 1 : Math.max(1, count)).fill(this.currentVariant?.id);
      let missingSlot = 0;
      if (count > 1 && this.dataset.allowMix === 'true') {
        for (let slot = 2; slot <= count; slot++) {
          const id = this.getSizeChoice(slot);
          if (!id && !missingSlot) missingSlot = slot;
          picked.push(id);
        }
      }
      const grouped = new Map();
      picked.filter(Boolean).forEach((id) => grouped.set(String(id), (grouped.get(String(id)) || 0) + 1));
      const fitsStock = [...grouped].every(([id, qty]) =>
        qty <= (limits.find((item) => String(item.id) === id)?.remaining || 0)
      );
      this.syncDeliveryEstimate(choice, picked, missingSlot);
      const ready = active && optionsReady && count > 0 && !missingSlot && fitsStock && this.dataset.variantPending !== 'true';
      this.dataset.ready = ready ? 'true' : 'false';
      this.syncSteps(ready);
      const choices = this.querySelector('[role="radiogroup"]');
      if (choices) choices.dataset.locked = optionsReady ? 'false' : 'true';
      for (const radio of this.quantityChoices) {
        const remaining = this.dataset.allowMix === 'true'
          ? limits.reduce((sum, item) => sum + item.remaining, 0)
          : limits.find((item) => String(item.id) === String(this.currentVariant?.id))?.remaining || 0;
        radio.disabled = !optionsReady || Number(radio.value) > remaining;
      }
      this.syncSizeMenus();
      const enabledChoice = this.querySelector('[data-quantity-choice]:checked:not(:disabled)');
      if (enabledChoice && this.input?.value !== enabledChoice.value) this.setQuantity();
      if (count) this.clearGroupError(choices);
      const extraSizes = this.querySelector('[data-quantity-sizes]');
      if (extraSizes) extraSizes.hidden = !active || !optionsReady || count < 2;
      this.setSizesExpanded(active && optionsReady && this.isMixedChoice());
      this.setPanelExpanded('[data-purchase-reveal]', active && optionsReady && count > 0);
      for (const row of this.querySelectorAll('[data-size-row]')) {
        const id = this.getSizeChoice(row.dataset.sizeRow);
        const rowFits = id && (grouped.get(String(id)) || 0) <= (limits.find((item) => String(item.id) === String(id))?.remaining || 0);
        if (Number(row.dataset.sizeRow) > count || rowFits) this.clearGroupError(row);
        const hint = row.querySelector('[data-size-hint]');
        if (hint) {
          const pending = this.dataset.variantPending === 'true';
          hint.textContent = pending ? 'Updating…' : id && !rowFits ? 'Unavailable' : 'Required';
          hint.hidden = Number(row.dataset.sizeRow) > count || (!pending && !!rowFits) || row.dataset.invalid === 'true';
        }
      }
      const guidance = this.querySelector('[data-guidance]');
      if (guidance) {
        guidance.hidden = !active || (optionsReady && count > 0);
        guidance.textContent = !optionsReady ? `Choose your ${this.dataset.selectionNoun} first.`
          : !count ? 'Choose a quantity.'
          : '';
      }
      const section = this.closest('.shopify-section');
      const buttons = [
        this.form.querySelector('[ref="addToCartButton"]'),
        section?.querySelector(`sticky-add-to-cart[data-product-id="${this.dataset.productId}"] [ref="addToCartButton"]`),
      ];
      for (const button of buttons) {
        if (button) button.disabled = !this.currentVariant?.available;
      }
      if (active) this.positionInventory();
    }

    positionInventory() {
      const section = this.closest('.shopify-section');
      const stock = section?.querySelector(`product-inventory[data-product-id="${this.dataset.productId}"]`);
      const buttons = this.form?.querySelector('.product-form-buttons');
      if (stock && buttons && stock.nextElementSibling !== buttons) {
        buttons.insertAdjacentElement('beforebegin', stock);
        stock.classList.add('product-inventory--guided-purchase');
      }
    }

    syncDeliveryEstimate(choice, picked, missingSlot) {
      const estimate = this.closest('.shopify-section')?.querySelector(
        `product-inventory[data-product-id="${this.dataset.productId}"] delivery-estimate`
      );
      if (!estimate) return;
      const threshold = Number(estimate.dataset.freeThresholdCents || 0);
      const ids = choice && !missingSlot ? picked : [this.currentVariant?.id];
      const percent = choice && !missingSlot ? Number(choice.dataset.discountPercent || 0) : this.cartDiscountForQuantity(1);
      const addedTotal = ids.reduce((sum, id) => {
        const price = Number(this.variants.find((variant) => String(variant.id) === String(id))?.price || 0);
        return sum + price - Math.floor(price * percent / 100);
      }, 0);
      const existingTotal = this.variants.reduce((sum, variant) => {
        const price = Number(variant.price);
        return sum + Number(variant.cart_quantity || 0) * (price - Math.floor(price * percent / 100));
      }, 0);
      const total = addedTotal + existingTotal;
      const free = threshold > 0 && total >= threshold ? 'true' : 'false';
      if (estimate.dataset.free !== free) {
        estimate.dataset.free = free;
        estimate.compute?.();
      }
    }

    updateExtraSizeAvailability(limits, count) {
      for (let slot = 2; slot <= count; slot++) {
        const row = this.querySelector(`[data-size-slot="${slot}"]`);
        if (!row) continue;
        for (const radio of row.querySelectorAll('[data-size-choice]')) {
          const variant = this.variants.find((item) => String(item.id) === radio.value);
          const alreadyChosen = Number(String(this.currentVariant?.id) === radio.value);
          let otherChosen = 0;
          for (let other = 2; other <= count; other++) {
            if (other !== slot && this.getSizeChoice(other) === radio.value) otherChosen++;
          }
          const remaining = limits.find((item) => String(item.id) === radio.value)?.remaining || 0;
          radio.disabled = !variant?.available || alreadyChosen + otherChosen + 1 > remaining;
        }
      }
    }

    syncCartQuantities(cart) {
      for (const variant of this.variants) {
        variant.cart_quantity = cart.items
          .filter((item) => String(item.variant_id) === String(variant.id))
          .reduce((sum, item) => sum + item.quantity, 0);
      }
      this.renderSelection();
      this.syncGuidedState();
    }

    isMixedChoice() {
      return this.dataset.allowMix === 'true' && this.selectedCount > 1 && !!this.querySelector('[data-quantity-sizes]') && !this.hidden;
    }

    setGroupError(group, message) {
      if (!group) return;
      group.dataset.invalid = 'true';
      group.setAttribute('aria-invalid', 'true');
      const error = group.hasAttribute('data-size-slot') ? group.querySelector('[data-choice-error]') : this.querySelector('[data-quantity-error]');
      if (error) { error.textContent = message; error.hidden = false; }
      const hint = group.querySelector('[data-size-hint]');
      if (hint) hint.hidden = true;
    }

    clearGroupError(group) {
      if (!group) return;
      delete group.dataset.invalid;
      group.removeAttribute('aria-invalid');
      const error = group.hasAttribute('data-size-slot') ? group.querySelector('[data-choice-error]') : this.querySelector('[data-quantity-error]');
      if (error) { error.textContent = ''; error.hidden = true; }
    }

    focusChoice(group) {
      if (!group) return;
      group.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'center' });
      const control = group.querySelector('[data-size-menu] summary') || group.querySelector('input:not(:disabled)');
      // Pointer users keep the red field error without a programmatic focus ring (double border).
      if (this.pointerModality) return;
      if (control) control.focus({ preventScroll: true });
      else { group.tabIndex = -1; group.focus({ preventScroll: true }); }
    }

    showPending() {
      if (this.selectedCount > 1) {
        const row = this.querySelector('[data-size-row]:not([hidden])');
        const hint = row?.querySelector('[data-size-hint]');
        if (hint) { this.clearGroupError(row); hint.hidden = false; hint.textContent = 'Updating…'; }
        return;
      }
      const guidance = this.querySelector('[data-guidance]');
      if (guidance) { guidance.hidden = false; guidance.textContent = 'Updating size…'; }
    }

    validateSelection() {
      if (!this.isGuidedChoice()) return true;
      if (!this.optionsReady()) {
        document.dispatchEvent(new CustomEvent('pusa:request-size', { detail: { productId: this.dataset.productId } }));
        return false;
      }
      const choice = this.querySelector('[data-quantity-choice]:checked:not(:disabled)');
      if (!choice) {
        const group = this.querySelector('.pdp-quantity-upsell__choices');
        this.setGroupError(group, 'Please choose your quantity.');
        this.focusChoice(group);
        return false;
      }
      if (this.dataset.variantPending === 'true') { this.showPending(); return false; }
      return !!this.getCartItems();
    }

    getCartItems() {
      const primary = this.currentVariant;
      if (!primary) {
        this.showError(`Choose the first ${this.dataset.itemNoun} ${this.dataset.selectionNoun}.`);
        return null;
      }
      const ids = Array(this.isMixedChoice() ? 1 : Math.max(1, this.selectedCount)).fill(String(primary.id));
      let firstMissing = null;
      for (let slot = 2; this.isMixedChoice() && slot <= this.selectedCount; slot++) {
        const row = this.querySelector(`[data-size-slot="${slot}"]`);
        const id = this.getSizeChoice(slot);
        if (!id) {
          this.setGroupError(row, `Choose your ${this.dataset.selectionNoun}.`);
          if (!firstMissing) firstMissing = row || this.querySelector('.pdp-quantity-upsell__choices');
        } else {
          ids.push(id);
        }
      }
      if (firstMissing) { this.focusChoice(firstMissing); return null; }
      const grouped = new Map();
      for (const id of ids) grouped.set(id, (grouped.get(id) || 0) + 1);
      for (const [id, count] of grouped) {
        const remaining = this.remainingByVariant.find((item) => String(item.id) === id)?.remaining || 0;
        if (count > remaining) {
          const rows = [...this.querySelectorAll('[data-size-slot]')].filter((row) => Number(row.dataset.sizeSlot) <= this.selectedCount && this.getSizeChoice(row.dataset.sizeSlot) === id);
          const groups = rows.length ? rows : [this.querySelector('.pdp-quantity-upsell__choices')];
          groups.forEach((group) => this.setGroupError(group, `${this.dataset.selectionNoun[0].toUpperCase()}${this.dataset.selectionNoun.slice(1)} unavailable.`));
          this.focusChoice(groups[0]);
          return null;
        }
      }
      return [...grouped].map(([variantId, quantity]) => ({ variantId, quantity }));
    }

    getSizeSummary() {
      if (!this.isMixedChoice()) return '';
      const sizes = [this.currentVariant?.title || ''];
      for (let slot = 2; slot <= this.selectedCount; slot++) {
        sizes.push(this.variants.find((variant) => String(variant.id) === this.getSizeChoice(slot))?.title || '');
      }
      return sizes.filter(Boolean).join(' + ');
    }

    showError(message) {
      const error = this.querySelector('[data-size-error]');
      if (!error) return;
      error.textContent = message;
      error.hidden = !message;
    }

    setQuantity() {
      const selected = this.querySelector('[data-quantity-choice]:checked:not(:disabled)');
      const selector = this.form?.querySelector('quantity-selector-component');
      if (!selected || !selector) return;
      selector.setValue(selected.value);
      selector.updateButtonStates();
      selector.onQuantityChange();
    }
  });
}
